import { useState, useEffect } from 'react';
import { Download, FileText } from 'react-feather';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/ui/data-table';
import { TableCard } from '@/components/ui/table-card';
import { brandTaxComplianceService, BrandAffiliateTaxInfo } from '../../../services/brand-tax-compliance.service';
import { toast } from 'react-hot-toast';
import { formatCurrency } from '../../../utils/formatters';
import { ReAuthenticationModal } from '../../../components/brand/tax-compliance/ReAuthenticationModal';
import { TaxInfoViewDrawer } from '../../../components/brand/tax-compliance/TaxInfoViewDrawer';
import { ExportConfirmationModal } from '../../../components/brand/tax-compliance/ExportConfirmationModal';
import { Button } from '@/components/ui/button';
import { SpinnerBlock } from '@/components/ui/spinner';
import { EmptyState } from '@/components/ui/empty-state';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export function Compliance() {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [affiliates, setAffiliates] = useState<BrandAffiliateTaxInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAffiliate, setSelectedAffiliate] = useState<BrandAffiliateTaxInfo | null>(null);
  const [showViewDrawer, setShowViewDrawer] = useState(false);
  const [showReAuthModal, setShowReAuthModal] = useState(false);
  const [showReAuthModalForExport, setShowReAuthModalForExport] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthenticatedForExport, setIsAuthenticatedForExport] = useState(false);
  const [exportAffiliateId, setExportAffiliateId] = useState<string | undefined>(undefined);

  // Generate list of years (current year and past 5 years)
  const availableYears = Array.from({ length: 6 }, (_, i) => currentYear - i);

  useEffect(() => {
    fetchAffiliates();
  }, [selectedYear]);

  const fetchAffiliates = async () => {
    try {
      setIsLoading(true);
      const data = await brandTaxComplianceService.getAffiliatesTaxInfo(selectedYear);
      setAffiliates(data);
    } catch (error: any) {
      console.error('Error fetching affiliates tax info:', error);
      toast.error(error?.response?.data?.message || 'Failed to load tax information');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewTaxInfo = async (affiliate: BrandAffiliateTaxInfo) => {
    if (!isAuthenticated) {
      setSelectedAffiliate(affiliate);
      setShowReAuthModal(true);
      return;
    }

    setSelectedAffiliate(affiliate);
    setShowViewDrawer(true);
  };

  const handleReAuthSuccess = () => {
    setIsAuthenticated(true);
    setShowReAuthModal(false);
    if (selectedAffiliate) {
      setShowViewDrawer(true);
    }
  };

  const handleReAuthSuccessForExport = () => {
    setIsAuthenticatedForExport(true);
    setShowReAuthModalForExport(false);
    // After successful re-auth, show export confirmation modal
    setShowExportModal(true);
  };

  const handleExport = (affiliateId?: string) => {
    // First require re-authentication for export
    setExportAffiliateId(affiliateId);
    if (!isAuthenticatedForExport) {
      setShowReAuthModalForExport(true);
      return;
    }
    // If already authenticated, show export confirmation
    setShowExportModal(true);
  };

  const handleConfirmExport = async () => {
    try {
      const blob = await brandTaxComplianceService.exportTaxInfo(selectedYear, exportAffiliateId);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reveshare_tax_export_${selectedYear}${exportAffiliateId ? '_single' : ''}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Tax information exported successfully');
      setShowExportModal(false);
      setExportAffiliateId(undefined);
      setIsAuthenticatedForExport(false); // Reset auth state after export
    } catch (error: any) {
      console.error('Error exporting tax info:', error);
      toast.error(error?.response?.data?.message || 'Failed to export tax information');
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const complianceColumns: ColumnDef<BrandAffiliateTaxInfo, any>[] = [
    {
      id: 'affiliate',
      header: 'Affiliate',
      cell: ({ row }) => (
        <span className="truncate block font-medium text-foreground">
          {row.original.firstName} {row.original.lastName}
        </span>
      ),
      meta: { width: 'w-[20%]' },
    },
    {
      id: 'email',
      header: 'Email',
      cell: ({ row }) => (
        <span className="truncate block text-muted-foreground">
          {row.original.email}
        </span>
      ),
      meta: { width: 'w-[24%]' },
    },
    {
      id: 'formType',
      header: 'Form Type',
      cell: ({ row }) => row.original.taxProfile.formType,
      meta: { width: 'w-28' },
    },
    {
      id: 'taxId',
      header: 'Tax ID (Last 4)',
      cell: ({ row }) =>
        row.original.taxProfile.taxIdLast4
          ? `XXX-XX-${row.original.taxProfile.taxIdLast4}`
          : 'N/A',
      meta: { width: 'w-32' },
    },
    {
      id: 'totalPaid',
      header: 'Total Paid',
      cell: ({ row }) => formatCurrency(row.original.totalPaid),
      meta: { width: 'w-28' },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => handleExport(row.original.id)}
          className="h-8 w-8 text-brand-600 hover:bg-brand-50 hover:text-brand-700 dark:hover:bg-brand-950/40"
          aria-label="Export"
          title="Export"
        >
          <Download className="w-4 h-4" />
        </Button>
      ),
      meta: { width: 'w-28' },
    },
  ];

  if (isLoading) {
    return <SpinnerBlock label="Loading tax information" />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Tax Compliance</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            View and export affiliate tax information by tax year
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* Year Filter */}
          <div className="flex items-center gap-2">
            <label htmlFor="year-filter" className="text-sm font-medium text-muted-foreground">
              Tax Year:
            </label>
            <Select
              value={String(selectedYear)}
              onValueChange={(value) => {
                setSelectedYear(Number(value));
                setIsAuthenticated(false); // Reset auth when year changes
                setIsAuthenticatedForExport(false); // Reset export auth when year changes
              }}
            >
              <SelectTrigger id="year-filter" className="w-28 bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableYears.map((year) => (
                  <SelectItem key={year} value={String(year)}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {affiliates.length > 0 && (
            <Button onClick={() => handleExport()}>
              <Download className="w-4 h-4" />
              Export All
            </Button>
          )}
        </div>
      </div>

      {affiliates.length === 0 ? (
        <EmptyState
          icon={<FileText />}
          title={`No tax information available for ${selectedYear}`}
        />
      ) : (
        <TableCard>
          <DataTable<BrandAffiliateTaxInfo>
            columns={complianceColumns}
            data={affiliates}
            emptyText={`No tax information available for ${selectedYear}`}
            getRowId={(row) => row.id}
            onRowClick={handleViewTaxInfo}
          />
        </TableCard>
      )}

      {/* Modals */}
      {showReAuthModal && (
        <ReAuthenticationModal
          isOpen={showReAuthModal}
          onClose={() => {
            setShowReAuthModal(false);
            setSelectedAffiliate(null);
          }}
          onSuccess={handleReAuthSuccess}
        />
      )}

      {showReAuthModalForExport && (
        <ReAuthenticationModal
          isOpen={showReAuthModalForExport}
          onClose={() => {
            setShowReAuthModalForExport(false);
            setExportAffiliateId(undefined);
          }}
          onSuccess={handleReAuthSuccessForExport}
        />
      )}

      {showViewDrawer && selectedAffiliate && (
        <TaxInfoViewDrawer
          isOpen={showViewDrawer}
          onClose={() => {
            setShowViewDrawer(false);
            setSelectedAffiliate(null);
            setIsAuthenticated(false); // Reset auth state
          }}
          affiliateId={selectedAffiliate.id}
          taxYear={selectedYear}
        />
      )}

      {showExportModal && isAuthenticatedForExport && (
        <ExportConfirmationModal
          isOpen={showExportModal}
          onClose={() => {
            setShowExportModal(false);
            setExportAffiliateId(undefined);
            setIsAuthenticatedForExport(false); // Reset auth state when closing
          }}
          onConfirm={handleConfirmExport}
        />
      )}
    </div>
  );
}

